import { useState, useCallback, useRef } from "react";
import { getConnectionLogs } from "../api/accounting.api";

export function useSyncLogs() {
  const [logs, setLogs] = useState([]);
  const [logsTotalCount, setLogsTotalCount] = useState(0);
  const [logsCurrentPage, setLogsCurrentPage] = useState(1);
  const [logsPerPage, setLogsPerPage] = useState(25);
  const [refreshingLogs, setRefreshingLogs] = useState(false);
  const [logsSearchQuery, setLogsSearchQuery] = useState("");
  const [logsSortBy, setLogsSortBy] = useState("");
  const activeFetchIdRef = useRef(0);
  const lastFetchRef = useRef(null);

  const fetchLogs = useCallback(async (connectionId, page = 1, limit = 25, search = "", sortBy = "") => {
    if (!connectionId) {
      setLogs([]);
      setLogsTotalCount(0);
      return;
    }

    const cacheKey = `${connectionId}-${page}-${limit}-${search}-${sortBy}`;
    const now = Date.now();

    // Prevent duplicate network calls within 500ms for identical parameters
    if (
      lastFetchRef.current &&
      lastFetchRef.current.key === cacheKey &&
      now - lastFetchRef.current.timestamp < 500
    ) {
      return lastFetchRef.current.promise;
    }

    const fetchId = ++activeFetchIdRef.current;

    const fetchPromise = (async () => {
      try {
        setRefreshingLogs(true);
        const offset = (page - 1) * limit;
        const logData = await getConnectionLogs(connectionId, offset, limit, search, sortBy);

        if (fetchId !== activeFetchIdRef.current) return;

        let rows = [];
        let count = 0;

        if (logData) {
          if (Array.isArray(logData)) {
            rows = logData;
            count = logData.length;
          } else {
            rows = logData.rows || [];
            count = logData.count || 0;
          }
        }

        setLogs(rows);
        setLogsTotalCount(count);
        setLogsCurrentPage(page);
        setLogsPerPage(limit);
        setLogsSearchQuery(search);
        setLogsSortBy(sortBy);
      } catch (err) {
        console.error("Failed to load sync logs:", err);
        if (fetchId === activeFetchIdRef.current) {
          setLogs([]);
          setLogsTotalCount(0);
        }
      } finally {
        if (fetchId === activeFetchIdRef.current) {
          setRefreshingLogs(false);
        }
      }
    })();

    lastFetchRef.current = { key: cacheKey, timestamp: now, promise: fetchPromise };
    return fetchPromise;
  }, []);

  return {
    logs,
    logsTotalCount,
    logsCurrentPage,
    logsPerPage,
    refreshingLogs,
    logsSearchQuery,
    setLogsSearchQuery,
    logsSortBy,
    fetchLogs
  };
}
